import React, { useMemo } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import Slider from "theme/components/organisms/Slider";
import { useIntl, defineMessages } from "react-intl";
import Icon from "theme/components/atoms/Icon";

const messages = defineMessages({
  previous: {
    id: "components.organisms.ItemsSlider.previous",
    defaultMessage: "Previous",
  },
  next: {
    id: "components.organisms.ItemsSlider.next",
    defaultMessage: "Next",
  },
});

const chunk = (length, children) => {
  let chunks = [];
  let currentChunk = [];
  let currentChunkLength = 0;
  for (let i = 0; i < children.length; i++) {
    const child = children[i];
    if (!child) {
      continue;
    }
    currentChunk.push(child);
    currentChunkLength++;

    if (currentChunkLength === length) {
      chunks.push(currentChunk);
      currentChunk = [];
      currentChunkLength = 0;
    }
  }
  if (currentChunkLength > 0) {
    chunks.push(currentChunk);
  }
  return chunks;
};

const Item = ({ itemsPerSlide, children, shouldFillChunks }) => (
  <div className="items-slider__item">
    {children
      .map((item, index) => (
        <div
          className="items-slider__element"
          key={item.key || `${index}-item`}
        >
          {item}
        </div>
      ))
      .concat(
        shouldFillChunks
          ? new Array(itemsPerSlide - children.length)
              .fill(null)
              .map((_, index) => {
                return (
                  <div
                    key={`empty-${index}`}
                    className="items-slider__element"
                  ></div>
                );
              })
          : []
      )}
  </div>
);

const ItemsSlider = ({ children, sliderProps, itemsPerSlide, appearance }) => {
  const intl = useIntl();
  const chunks = useMemo(
    () => chunk(itemsPerSlide, children),
    [children, itemsPerSlide]
  );

  const className = classNames("items-slider", {
    [`items-slider--${appearance}`]: appearance,
  });

  return (
    <div className={className}>
      <div className={classNames({ container: appearance !== "vertical" })}>
        <div className="items-slider__container">
          <div className="items-slider__slider">
            <Slider
              appearance="overflow-visible"
              direction={appearance === "vertical" ? "vertical" : undefined}
              {...sliderProps}
            >
              {chunks.map((chunk, index) => (
                <Item
                  itemsPerSlide={itemsPerSlide}
                  shouldFillChunks={
                    appearance === "vertical" || chunks.length > 1
                  }
                  key={index}
                >
                  {chunk}
                </Item>
              ))}
            </Slider>
          </div>
          {sliderProps.state.nextIndex > 0 ||
          sliderProps.state.nextIndex < chunks.length - 1 ? (
            <div className="items-slider__controls">
              <div className="items-slider__previous">
                <button
                  onClick={sliderProps.showPrev}
                  disabled={sliderProps.state.nextIndex === 0}
                >
                  <Icon
                    icon="chevron-left"
                    title={intl.formatMessage(messages.previous)}
                  />
                </button>
              </div>
              <div className="items-slider__next">
                <button
                  onClick={sliderProps.showNext}
                  disabled={sliderProps.state.nextIndex === chunks.length - 1}
                >
                  <Icon
                    icon="chevron-right"
                    title={intl.formatMessage(messages.next)}
                  />
                </button>
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

ItemsSlider.propTypes = {
  appearance: PropTypes.oneOf(["default", "vertical"]),
};

export default ItemsSlider;
